import type { OnboardingSubscriptionPlan } from '@houzy/schemas/partner-cockpit/onboarding';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessageKey } from '../../../lib/api';
import { usePlans } from '../api/plansApi';
import { useSelectPlanMutation } from '../api/subscriptionApi';
import { OnboardingFooter } from '../components/OnboardingFooter';
import { OnboardingStepContent } from '../components/OnboardingStepContent';
import { OnboardingStepHeading } from '../components/OnboardingStepHeading';
import { PlanBenefitsPanel } from '../components/PlanCardParts';
import { SubscriptionPlanCard } from '../components/SubscriptionPlanCard';
import { defaultCadenceOf } from '../planPresentation';

// The design draws the three cards across 900px. OnboardingStepContent subtracts its own 24px gutters, so the row
// only reaches that width if they are added back; otherwise the cards narrow and the commitment line wraps.
const PLANS_MAX_WIDTH = 948;

export function SubscriptionPlanStep(): ReactElement {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: plans } = usePlans();
    const [choosingUuid, setChoosingUuid] = useState<string | undefined>(undefined);
    const selectPlan = useSelectPlanMutation();

    const choose = (plan: OnboardingSubscriptionPlan): void => {
        setChoosingUuid(plan.uuid);
        selectPlan.mutate(
            {
                planUuid: plan.uuid,
                billingCadence: defaultCadenceOf(plan),
                // Provisional on the Stripe path: the craftsman may pick TWINT inside the session, and the
                // setup_intent webhook records what was actually used.
                paymentMethod: plan.paymentMethods[0] ?? 'Card',
            },
            {
                onSuccess: () => void navigate({ to: '/onboarding/subscription/checkout' }),
                onError: () => setChoosingUuid(undefined),
            },
        );
    };

    return (
        <>
            <OnboardingStepContent maxWidth={PLANS_MAX_WIDTH}>
                <Stack spacing={6} sx={{ alignItems: 'center' }}>
                    <OnboardingStepHeading
                        title={t('ONBOARDING.SUBSCRIPTION.TITLE')}
                        description={t('ONBOARDING.SUBSCRIPTION.DESCRIPTION')}
                        align="center"
                    />

                    <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ width: '100%', alignItems: 'stretch' }}>
                            {plans.map((plan, index) => (
                                <SubscriptionPlanCard
                                    key={plan.uuid}
                                    plan={plan}
                                    index={index}
                                    loading={selectPlan.isPending && choosingUuid === plan.uuid}
                                    onChoose={() => choose(plan)}
                                />
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {t('ONBOARDING.SUBSCRIPTION.PRICES_EXCL_MWST')}
                        </Typography>
                    </Stack>

                    {selectPlan.isError && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {t(apiErrorMessageKey(selectPlan.error, 'ONBOARDING.SUBSCRIPTION.ERRORS.CHOOSE_FAILED'))}
                        </Alert>
                    )}

                    <PlanBenefitsPanel />
                </Stack>
            </OnboardingStepContent>

            <OnboardingFooter currentStep="subscription" hideNext />
        </>
    );
}
